import { useEffect, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

interface InterviewConsentGateProps {
  onConsentChange: (hasConsented: boolean) => void;
}

/**
 * Consent checkbox for the prep page when Interview Coach is not available.
 *
 * Preserves the prior non-coach contract from InterviewVideoConsent: the
 * checkbox alone is not enough — a 3s dwell must elapse before
 * onConsentChange(true) can fire.
 */
export const InterviewConsentGate = ({ onConsentChange }: InterviewConsentGateProps) => {
  const { t } = useTranslation('interview');
  const [dwellComplete, setDwellComplete] = useState(false);
  const [hasConsented, setHasConsented] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDwellComplete(true);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  const handleConsentChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newConsentValue = e.target.checked;
    setHasConsented(newConsentValue);
    onConsentChange(newConsentValue && dwellComplete);
  };

  useEffect(() => {
    onConsentChange(dwellComplete && hasConsented);
  }, [dwellComplete, hasConsented, onConsentChange]);

  return (
    <div className="w-full">
      <label className="flex items-start cursor-pointer group w-full px-5 py-4">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4 text-purple focus:ring-purple border-gray-300 rounded cursor-pointer flex-shrink-0"
          checked={hasConsented}
          onChange={handleConsentChange}
          id="privacy-consent-checkbox"
        />
        <span className="ml-3 text-sm text-dark-invirtus font-body font-medium leading-relaxed group-hover:text-black transition-colors">
          {t('consent.label')}
        </span>
      </label>
    </div>
  );
};
